import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Tooltip,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { BookingFormState, fmtCAD, n, LED_PRICING } from "./bookingTypes";
import type { PricingItem, DiscountResult } from "./pricingEngine";
import type { VendorServiceItem } from "./bookingTypes";

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS — match project style exactly
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy:    "#0f172a",
  navyMid: "#1e293b",
  gold:    "#d4a373",
  cream:   "#f8f5f0",
  white:   "#ffffff",
  bg:      "#f8f5f0",
  slate:   "#6b7280",
  slateLt: "#9ca3af",
  line:    "#e5e7eb",
  doneBg:  "#f8f5f0",
  doneTx:  "#6b7280",
  doneBd:  "#d4a373",
  redTx:   "#9b2c2c",
  redBg:   "#fef2f2",
  redBd:   "#fecaca",
  peach:   "#f8f5f0",
  inputBg: "#fafaf9",
  green:   "#16a34a",
  greenBg: "#f0fdf4",
  greenBd: "#bbf7d0",
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════════════════ */
interface Totals {
  hallSubtotal: number; foodSubtotal: number; barSubtotal: number;
  addonsSubtotal: number; vendorServicesSubtotal: number; ledSubtotal: number;
  baseSubtotal: number; adjustment: number;
  serviceChargeAmount: number; gratuityAmount: number;
  subtotal: number; hstAmount: number; grandTotal: number;
  guests: number; depositsPaid: number; balanceDue: number;
  discountResult: DiscountResult | null;
  discountApplied: number;
}

interface Props {
  totals: Totals;
  form: BookingFormState;
}

/* ═══════════════════════════════════════════════════════════════════════════
   FLASH HOOK — animate on value change
   ═══════════════════════════════════════════════════════════════════════════ */
const useFlash = (value: number) => {
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 400);
      prev.current = value;
      return () => clearTimeout(t);
    }
  }, [value]);
  return flash;
};

/* ═══════════════════════════════════════════════════════════════════════════
   STATUS COLORS
   ═══════════════════════════════════════════════════════════════════════════ */
const STATUS_STYLE = {
  discounted: { color: T.redTx, bg: T.redBg, bd: T.redBd, label: "Discounted" },
  increased:  { color: T.green, bg: T.greenBg, bd: T.greenBd, label: "Increased" },
  fixed:      { color: T.slateLt, bg: T.bg, bd: T.line, label: "Fixed" },
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   ICON MAP — modules + vendor service categories
   ═══════════════════════════════════════════════════════════════════════════ */
const ICON_MAP: Record<string, React.ReactNode> = {
  hall:          <MeetingRoomOutlinedIcon sx={{ fontSize: 13 }} />,
  food:          <RestaurantOutlinedIcon sx={{ fontSize: 13 }} />,
  bar:           <LocalBarOutlinedIcon sx={{ fontSize: 13 }} />,
  led:           <TvOutlinedIcon sx={{ fontSize: 13 }} />,
  addon:         <ExtensionOutlinedIcon sx={{ fontSize: 13 }} />,
  entertainment: <MusicNoteOutlinedIcon sx={{ fontSize: 13 }} />,
  decoration:    <BrushOutlinedIcon sx={{ fontSize: 13 }} />,
  bar_staff:     <LocalBarOutlinedIcon sx={{ fontSize: 13 }} />,
  av_led:        <TvOutlinedIcon sx={{ fontSize: 13 }} />,
  catering:      <RestaurantOutlinedIcon sx={{ fontSize: 13 }} />,
  vendor:        <StorefrontOutlinedIcon sx={{ fontSize: 13 }} />,
};

function getIcon(id: string | number): React.ReactNode {
  const key = String(id);
  if (ICON_MAP[key]) return ICON_MAP[key];
  if (key.startsWith("addon")) return ICON_MAP.addon;
  if (key.startsWith("vendor")) return ICON_MAP.vendor;
  return <TuneOutlinedIcon sx={{ fontSize: 13 }} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   PRICING ITEM ROW — base → final with arrow + color
   ═══════════════════════════════════════════════════════════════════════════ */
const PricingRow: React.FC<{ item: PricingItem; vendorMeta?: VendorServiceItem | null }> = ({ item, vendorMeta }) => {
  const style = STATUS_STYLE[item.price_status];
  const changed = item.price_status !== "fixed" && item.base_price !== item.final_price;
  const flash = useFlash(item.final_price);

  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      py: "6px", px: 1, borderRadius: "8px",
      transition: "all 0.15s",
      bgcolor: flash ? `${style.bg}` : "transparent",
      "&:hover": { bgcolor: `${T.bg}80` },
    }}>
      {/* Left: icon + label + vendor meta */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flex: 1, minWidth: 0 }}>
        <Box sx={{
          width: 22, height: 22, borderRadius: "6px", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: item.price_status === "fixed" ? T.bg : `${style.color}10`,
          color: item.price_status === "fixed" ? T.slateLt : style.color,
        }}>
          {item.price_status === "fixed" ? <LockOutlinedIcon sx={{ fontSize: 11 }} /> : getIcon(item.id)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 12, color: T.slate, fontWeight: 400, maxWidth: 110 }}>
            {item.label}
          </Typography>
          {vendorMeta && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography noWrap sx={{ fontSize: 9, color: T.slateLt, maxWidth: 80 }}>
                {vendorMeta.vendorName}
              </Typography>
              {vendorMeta.isDefaultVendor && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.2,
                  px: 0.4, py: 0, borderRadius: "3px",
                  bgcolor: T.greenBg, border: `1px solid ${T.greenBd}`,
                }}>
                  <Typography sx={{ fontSize: 7, fontWeight: 700, color: T.green, textTransform: "uppercase" }}>
                    default
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Right: price display */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        {changed ? (
          <>
            <Typography sx={{
              fontSize: 11, color: T.slateLt,
              textDecoration: "line-through", fontVariantNumeric: "tabular-nums",
            }}>
              {fmtCAD(item.base_price)}
            </Typography>
            <ArrowRightAltIcon sx={{ fontSize: 14, color: T.slateLt, mx: -0.2 }} />
            <Typography sx={{
              fontSize: 12, fontWeight: 700, color: style.color,
              fontVariantNumeric: "tabular-nums",
              transition: "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
              transform: flash ? "scale(1.1)" : "scale(1)",
            }}>
              {fmtCAD(item.final_price)}
            </Typography>
          </>
        ) : (
          <Typography sx={{
            fontSize: 12, fontWeight: 500,
            color: item.final_price > 0 ? T.navy : T.slateLt,
            fontVariantNumeric: "tabular-nums",
          }}>
            {fmtCAD(item.final_price)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SIMPLE ROW — for subtotals, charges, etc.
   ═══════════════════════════════════════════════════════════════════════════ */
const SimpleRow: React.FC<{
  label: string; value: number; color?: string; sm?: boolean; bold?: boolean; icon?: React.ReactNode;
}> = ({ label, value, color, sm, bold, icon }) => {
  const f = useFlash(value);
  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      py: sm ? "4px" : "7px", px: 1, borderRadius: "8px",
      "&:hover": { bgcolor: sm ? "transparent" : `${T.bg}80` },
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
        {icon && (
          <Box sx={{
            width: 22, height: 22, borderRadius: "6px",
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: value > 0 ? `${T.gold}12` : T.bg, color: value > 0 ? T.gold : T.slateLt, flexShrink: 0,
          }}>
            {icon}
          </Box>
        )}
        <Typography sx={{ fontSize: sm ? 11 : 12, color: color ?? T.slateLt, fontWeight: bold ? 600 : 400 }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{
        fontSize: sm ? 11 : 12, fontWeight: bold ? 700 : 500,
        color: color ?? (value > 0 ? T.navy : T.slateLt),
        transition: "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
        transform: f ? "scale(1.08)" : "scale(1)", fontVariantNumeric: "tabular-nums",
      }}>
        {fmtCAD(value)}
      </Typography>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION HEADER — collapsible
   ═══════════════════════════════════════════════════════════════════════════ */
const SectionHeader: React.FC<{
  icon: React.ReactNode; title: string; count: number;
  total: number; expanded: boolean; onToggle: () => void;
}> = ({ icon, title, count, total, expanded, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      py: "6px", px: 1, borderRadius: "8px", cursor: "pointer",
      "&:hover": { bgcolor: `${T.bg}80` },
      transition: "background 0.15s",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Box sx={{
        width: 22, height: 22, borderRadius: "6px",
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: `${T.gold}12`, color: T.gold, flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
        {title}
      </Typography>
      {count > 0 && (
        <Chip
          label={count}
          size="small"
          sx={{
            height: 16, minWidth: 16, fontSize: 9, fontWeight: 700,
            bgcolor: T.bg, color: T.slate,
            "& .MuiChip-label": { px: 0.4 },
          }}
        />
      )}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, fontVariantNumeric: "tabular-nums" }}>
        {fmtCAD(total)}
      </Typography>
      {expanded
        ? <ExpandLessIcon sx={{ fontSize: 14, color: T.slateLt }} />
        : <ExpandMoreIcon sx={{ fontSize: 14, color: T.slateLt }} />
      }
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   BREAKDOWN SECTION — shows section total + expandable child items
   ═══════════════════════════════════════════════════════════════════════════ */
interface BItem { label: string; formula?: string; total: number; }
interface BSection { name: string; icon: React.ReactNode; total: number; items: BItem[]; }

function buildFormBreakdown(form: BookingFormState, totals: Totals): BSection[] {
  const g = totals.guests;
  const sections: BSection[] = [];

  // Hall
  if (totals.hallSubtotal > 0) {
    const items: BItem[] = [];
    const hallName = form.halls[0]?.hallLabel || form.halls[0]?.hallKey || "Hall";
    if (n(form.hallFlatFee) > 0) items.push({ label: `${hallName} — Flat Fee`, total: n(form.hallFlatFee) });
    if (n(form.hallPricePerPerson) > 0) items.push({ label: `${hallName} — Per Person`, formula: `${fmtCAD(n(form.hallPricePerPerson))} × ${g} guests`, total: n(form.hallPricePerPerson) * g });
    sections.push({ name: "Hall Rental", icon: <MeetingRoomOutlinedIcon sx={{ fontSize: 13 }} />, total: totals.hallSubtotal, items });
  }

  // Food
  if (totals.foodSubtotal > 0) {
    const items: BItem[] = [];
    if (form.foodServiceOption && n(form.foodServicePricePerPerson) > 0) items.push({ label: form.foodServiceOption, formula: `${fmtCAD(n(form.foodServicePricePerPerson))}/pp × ${g}`, total: n(form.foodServicePricePerPerson) * g });
    if (form.menuOption && n(form.menuPricePerPerson) > 0) items.push({ label: form.menuOption, formula: `${fmtCAD(n(form.menuPricePerPerson))}/pp × ${g}`, total: n(form.menuPricePerPerson) * g });
    sections.push({ name: "Food & Menu", icon: <RestaurantOutlinedIcon sx={{ fontSize: 13 }} />, total: totals.foodSubtotal, items });
  }

  // Bar
  if (totals.barSubtotal > 0) {
    const items: BItem[] = [];
    if (form.barServiceOption && n(form.barServiceAmount) > 0) items.push({ label: form.barServiceOption, formula: "Flat", total: n(form.barServiceAmount) });
    if (form.barPackageOption && n(form.barPackagePricePerPerson) > 0) items.push({ label: form.barPackageOption, formula: `${fmtCAD(n(form.barPackagePricePerPerson))}/pp × ${g}`, total: n(form.barPackagePricePerPerson) * g });
    if (n(form.bartenderCount) > 0 && n(form.bartenderAmount) > 0) items.push({ label: `${form.bartenderCount} Bartender${n(form.bartenderCount) > 1 ? "s" : ""}`, formula: `${fmtCAD(n(form.bartenderAmount))} × ${form.bartenderCount}${n(form.barHours) > 0 ? ` (${form.barHours} hrs)` : ""}`, total: n(form.bartenderCount) * n(form.bartenderAmount) });
    sections.push({ name: "Bar Service", icon: <LocalBarOutlinedIcon sx={{ fontSize: 13 }} />, total: totals.barSubtotal, items });
  }

  // LED
  if (form.selectedLEDs.length > 0) {
    const items = form.selectedLEDs.map(led => ({ label: led, total: LED_PRICING[led] ?? 0 }));
    sections.push({ name: "LED / AV", icon: <TvOutlinedIcon sx={{ fontSize: 13 }} />, total: totals.ledSubtotal, items });
  }

  // Add-ons
  if (form.addons.length > 0) {
    const items = form.addons.map(a => ({ label: a.itemName, formula: a.quantity > 1 ? `${fmtCAD(a.unitPrice)} × ${a.quantity}` : undefined, total: a.lineTotal }));
    sections.push({ name: "Add-ons", icon: <ExtensionOutlinedIcon sx={{ fontSize: 13 }} />, total: totals.addonsSubtotal, items });
  }

  // Vendor Services grouped by category
  if (form.vendorServices.length > 0) {
    const catMap = new Map<string, VendorServiceItem[]>();
    form.vendorServices.forEach(vs => {
      const cat = vs.serviceName || "Vendor";
      if (!catMap.has(cat)) catMap.set(cat, []);
      catMap.get(cat)!.push(vs);
    });
    catMap.forEach((vItems, cat) => {
      const items: BItem[] = vItems.map(vs => {
        const f = vs.pricingType === "per_person" ? `${fmtCAD(vs.rate)}/pp × ${vs.quantity}` : vs.pricingType === "per_hour" ? `${fmtCAD(vs.rate)}/hr × ${vs.hours}` : vs.pricingType === "per_event" ? "Per Event" : `${fmtCAD(vs.rate)} × ${vs.quantity}`;
        return { label: `${vs.itemName} — ${vs.vendorName}`, formula: f, total: vs.finalPrice };
      });
      sections.push({ name: cat, icon: <StorefrontOutlinedIcon sx={{ fontSize: 13 }} />, total: items.reduce((s, i) => s + i.total, 0), items });
    });
  }

  return sections;
}

const BreakdownRow: React.FC<{ section: BSection }> = ({ section }) => {
  // Always show items — no collapse, no hidden state
  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Section header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: "4px", px: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Box sx={{ width: 20, height: 20, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${T.gold}12`, color: T.gold, flexShrink: 0 }}>
            {section.icon}
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.navy }}>{section.name}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.navy, fontVariantNumeric: "tabular-nums" }}>
          {fmtCAD(section.total)}
        </Typography>
      </Box>

      {/* Always-visible line items */}
      {section.items.length > 0 && (
        <Box sx={{ pl: 3.5, pr: 0.5 }}>
          {section.items.map((item, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", py: "2px" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 11, color: T.slate, fontWeight: 400 }}>{item.label}</Typography>
                {item.formula && (
                  <Typography sx={{ fontSize: 9, color: T.slateLt, lineHeight: 1.3 }}>{item.formula}</Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 11, fontWeight: 500, color: T.slate, fontVariantNumeric: "tabular-nums", flexShrink: 0, ml: 1 }}>
                {fmtCAD(item.total)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Thin separator */}
      <Box sx={{ height: 1, bgcolor: T.line, mx: 0.5, mt: 0.8, opacity: 0.5 }} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT: BookingSummary
   ═══════════════════════════════════════════════════════════════════════════ */
const BookingSummary: React.FC<Props> = ({ totals, form }) => {
  const totalFlash = useFlash(totals.grandTotal);
  const [modulesOpen, setModulesOpen] = useState(true);
  const [vendorsOpen, setVendorsOpen] = useState(true);

  // Detailed breakdown sections from live form data
  const formBreakdown = React.useMemo(() => buildFormBreakdown(form, totals), [form, totals]);

  // ── Extract pricing engine items ──────────────────────────────────────
  const dr = totals.discountResult;
  const hasDiscount = dr && dr.discountApplied > 0;

  const moduleItems: PricingItem[] = dr
    ? dr.items.filter((i) => !String(i.id).startsWith("vendor-"))
    : [];
  const vendorItems: PricingItem[] = dr
    ? dr.items.filter((i) => String(i.id).startsWith("vendor-"))
    : [];

  // Lookup vendor metadata from form.vendorServices by itemId
  const getVendorMeta = (pricingItemId: string | number): VendorServiceItem | null => {
    const idStr = String(pricingItemId);
    if (!idStr.startsWith("vendor-")) return null;
    const itemId = Number(idStr.replace("vendor-", ""));
    return form.vendorServices.find((vs) => vs.itemId === itemId) ?? null;
  };

  // Fallback subtotals when no discount is active
  const modulesTotal = hasDiscount
    ? moduleItems.reduce((s, i) => s + i.final_price, 0)
    : totals.hallSubtotal + totals.foodSubtotal + totals.barSubtotal + totals.addonsSubtotal + totals.ledSubtotal;
  const vendorsTotal = hasDiscount
    ? vendorItems.reduce((s, i) => s + i.final_price, 0)
    : totals.vendorServicesSubtotal;

  const fixedVendorCount = vendorItems.filter((i) => i.price_status === "fixed").length;
  const hasFixedVendors = fixedVendorCount > 0 && hasDiscount;

  const hasAnyService = totals.hallSubtotal > 0 || totals.foodSubtotal > 0 || totals.barSubtotal > 0
    || totals.addonsSubtotal > 0 || totals.ledSubtotal > 0 || totals.vendorServicesSubtotal > 0
    || form.vendorServices.length > 0 || form.addons.length > 0;

  return (
    <Box sx={{
      background: `linear-gradient(180deg, ${T.white} 0%, #fdfcfa 100%)`,
      borderRadius: "16px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)",
      overflow: "hidden",
      transition: "box-shadow 0.3s",
      maxHeight: "calc(100vh - 120px)",
      display: "flex", flexDirection: "column",
      "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 12px 36px rgba(0,0,0,0.05)" },
    }}>

      {/* ── HEADER ── */}
      <Box sx={{
        px: 2.5, pt: 2.5, pb: 2,
        background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{
          position: "absolute", inset: 0, opacity: 0.04,
          backgroundImage: "radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }} />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.white, letterSpacing: "-0.01em" }}>
                Booking Summary
              </Typography>
            </Box>
            {totals.guests > 0 && (
              <Chip
                icon={<PeopleOutlinedIcon sx={{ fontSize: 12, color: `${T.gold} !important` }} />}
                label={`${totals.guests} guests`}
                size="small"
                sx={{
                  height: 24, fontSize: 10, fontWeight: 600,
                  bgcolor: "rgba(201,169,110,0.15)", color: T.gold,
                  border: "1px solid rgba(201,169,110,0.25)",
                  "& .MuiChip-icon": { ml: "4px" },
                }}
              />
            )}
          </Box>

          {/* Hall + Date chips */}
          {(form.halls.length > 0 || form.eventDate) && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {form.halls.map((h) => (
                <Chip
                  key={h.hallKey}
                  icon={<MeetingRoomOutlinedIcon sx={{ fontSize: 11, color: `${T.peach} !important` }} />}
                  label={h.hallLabel}
                  size="small"
                  sx={{
                    height: 22, fontSize: 10, fontWeight: 600,
                    bgcolor: "rgba(255,235,211,0.12)", color: T.peach,
                    "& .MuiChip-icon": { ml: "3px" },
                  }}
                />
              ))}
              {form.eventDate && (
                <Chip
                  icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 10, color: "rgba(255,255,255,0.5) !important" }} />}
                  label={new Date(form.eventDate + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
                  size="small"
                  sx={{
                    height: 22, fontSize: 10, fontWeight: 500,
                    bgcolor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)",
                    "& .MuiChip-icon": { ml: "3px" },
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── SCROLLABLE CONTENT ── */}
      <Box sx={{
        flex: 1, overflowY: "auto", overflowX: "hidden",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
        "&::-webkit-scrollbar-thumb": { bgcolor: "#d4d0c8", borderRadius: 2, "&:hover": { bgcolor: "#b0aca4" } },
      }}>

      {/* Empty state */}
      {!hasAnyService && (
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: T.slateLt }}>No services selected yet</Typography>
        </Box>
      )}

      {/* ── ITEMIZED BREAKDOWN (always shown) ── */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        {formBreakdown.map(section => (
          <BreakdownRow key={section.name} section={section} />
        ))}
      </Box>

      {/* Vendor services are now included in formBreakdown above */}

      {/* ── FIXED VENDOR WARNING ── */}
      {hasFixedVendors && (
        <Box sx={{ px: 2, pb: 0.5 }}>
          <Box sx={{
            display: "flex", alignItems: "flex-start", gap: 0.8,
            px: 1.2, py: 0.8, borderRadius: "8px",
            bgcolor: "#fefce8", border: "1px solid #fef08a",
          }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: "#a16207", mt: 0.1, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 10, color: "#92400e", lineHeight: 1.4 }}>
              {fixedVendorCount} vendor service{fixedVendorCount > 1 ? "s are" : " is"} fixed and not included in discount
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── DISCOUNT APPLIED ── */}
      {hasDiscount && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            py: "6px", px: 1, borderRadius: "8px",
            bgcolor: `${T.redBg}80`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: "6px",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: `${T.redTx}12`, color: T.redTx, flexShrink: 0,
              }}>
                <TuneOutlinedIcon sx={{ fontSize: 13 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.redTx }}>
                  Discount Applied
                </Typography>
                <Typography sx={{ fontSize: 9, color: T.slateLt }}>
                  {dr!.items.filter((i) => i.price_status === "discounted").length} of {dr!.items.length} items adjusted
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.redTx, fontVariantNumeric: "tabular-nums" }}>
              &minus;{fmtCAD(totals.discountApplied)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── POSITIVE ADJUSTMENT ── */}
      {totals.adjustment > 0 && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <SimpleRow
            label="Adjustment"
            value={totals.adjustment}
            color={T.doneTx}
            icon={<TuneOutlinedIcon sx={{ fontSize: 13 }} />}
          />
        </Box>
      )}

      {/* ── CHARGES ── */}
      {(totals.serviceChargeAmount > 0 || totals.gratuityAmount > 0) && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          {totals.serviceChargeAmount > 0 && (
            <SimpleRow label={`Service (${n(form.serviceChargePercent)}%)`} value={totals.serviceChargeAmount} sm />
          )}
          {totals.gratuityAmount > 0 && (
            <SimpleRow label={`Gratuity (${n(form.gratuityPercent)}%)`} value={totals.gratuityAmount} sm />
          )}
        </Box>
      )}

      {/* ── DIVIDER ── */}
      <Box sx={{ mx: 2.5, my: 0.5 }}>
        <Box sx={{ height: 1, background: `linear-gradient(90deg, transparent, ${T.line}, transparent)` }} />
      </Box>

      {/* ── SUBTOTAL + TAX ── */}
      <Box sx={{ px: 1.5, pb: 0.5 }}>
        <SimpleRow label="Subtotal" value={totals.subtotal} bold sm />
        <SimpleRow label="HST (13%)" value={totals.hstAmount} sm color={T.slateLt} />
      </Box>

      </Box>{/* end scrollable content */}

      {/* ── GRAND TOTAL ── */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          py: 1.8, px: 2,
          background: `linear-gradient(135deg, ${T.peach} 0%, #fff0d9 100%)`,
          borderRadius: "12px",
          border: `1px solid ${T.gold}20`,
          transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
          transform: totalFlash ? "scale(1.015)" : "scale(1)",
          boxShadow: totalFlash
            ? `0 0 0 2px ${T.gold}40, 0 4px 16px rgba(201,169,110,0.2)`
            : "0 1px 4px rgba(201,169,110,0.08)",
        }}>
          <Box>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: T.gold, textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.2 }}>
              Total
            </Typography>
            <Typography sx={{ fontSize: 11, color: T.slateLt, fontWeight: 400 }}>
              Inc. HST
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: 26, fontWeight: 800, color: T.navy,
            lineHeight: 1, letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
            transform: totalFlash ? "scale(1.04)" : "scale(1)",
          }}>
            {fmtCAD(totals.grandTotal)}
          </Typography>
        </Box>
      </Box>

      {/* ── DEPOSITS / BALANCE ── */}
      {totals.depositsPaid > 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ height: 1, bgcolor: T.line, mb: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px", px: 1 }}>
            <Typography sx={{ fontSize: 12, color: T.doneTx, fontWeight: 500 }}>Paid</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.doneTx, fontVariantNumeric: "tabular-nums" }}>
              {fmtCAD(totals.depositsPaid)}
            </Typography>
          </Box>
          <Box sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            py: "6px", px: 1.2,
            bgcolor: totals.balanceDue > 0 ? "#fdf5f5" : T.doneBg,
            borderRadius: "8px",
            border: `1px solid ${totals.balanceDue > 0 ? "#e5c5c5" : T.doneBd}`,
          }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: totals.balanceDue > 0 ? T.redTx : T.doneTx }}>
              Balance Due
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: totals.balanceDue > 0 ? T.redTx : T.doneTx, fontVariantNumeric: "tabular-nums" }}>
              {fmtCAD(totals.balanceDue)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BookingSummary;
